import { Component, HostListener, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MessageDialogService } from '../../services/message-dialog';
import { DeliveryService, AssignedLoadsRequestPayload, LoadDetailsResponse, DeliveryLoad, UpdatableLoadState } from '../../services/delivery.service';
import { LoadViewerComponent } from '../../components/load-viewer-component/load-viewer-component';
import { FilteredLoad, LoadManagementService } from '../../services/load-management.service';
import { LoadTableComponent } from '../../components/load-table-component/load-table-component';
import { SelectedProduct } from '../../services/products';
import { Customer, CustomerService } from '../../services/customer';
import { firstValueFrom } from 'rxjs';
import { InvoicesReportTableComponent } from '../../components/invoices-report-table-component/invocies-report-table-component';

@Component({
  selector: 'app-delivery-load-report',
  standalone: true,
  templateUrl: './delivery-load-report-component.html',
  styleUrls: ['./delivery-load-report-component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    InvoicesReportTableComponent,
    LoadTableComponent
  ]
})
export class DeliveryLoadReportComponent implements OnInit {
  private readonly loadDetailsHistoryStateKey = 'a1-route-sales-load-details';
  private readonly invoiceDetailsHistoryStateKey = 'a1-route-sales-invoice-details';
  private readonly invoiceReturnHistoryStateKey = 'a1-route-sales-invoice-return';
  private readonly lastOpenLoadIdStorageKey = 'a1-route-sales-last-open-load-id';
  startDate: string = '';
  endDate: string = '';
  loads: FilteredLoad[] = [];

  page: number = 1;
  pageSize: number = 10;
  totalPages: number = 1;

  selectedLoad: LoadDetailsResponse | null = null;
  selectedProducts: SelectedProduct[] = [];
  private _showCart = false;

  get showCart(): boolean {
    return this._showCart;
  }

  set showCart(value: boolean) {
    this._showCart = value;
    // Reload loads when showCart changes to false
    if (!value) {
      this.fetchAssignedLoads();
    }
  }

  constructor(
    private deliveryService: DeliveryService,
    private loadService: LoadManagementService,
    private messageDialogService: MessageDialogService,
    private customerService: CustomerService
  ) {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    this.startDate = today.toISOString().split('T')[0];
    this.endDate = tomorrow.toISOString().split('T')[0];
  }

  ngOnInit(): void {
    const lastOpenLoadId = Number(sessionStorage.getItem(this.lastOpenLoadIdStorageKey));
    if (Number.isFinite(lastOpenLoadId) && lastOpenLoadId > 0) {
      this.openLoadDetails(lastOpenLoadId, true);
    }
  }

  fetchAssignedLoads(): void {
    if (!this.startDate || !this.endDate) {
      this.messageDialogService.warning('Please select start and end dates.');
      return;
    }

    const payload: AssignedLoadsRequestPayload & { page: number; page_size: number } = {
      startDate: this.startDate,
      endDate: this.endDate,
      page: this.page,
      page_size: this.pageSize
    };

    this.deliveryService.getAssignedLoads(payload).subscribe((res) => {
      if (res.status === 'success' && res.loads) {
        this.loads = res.loads;
        this.totalPages = res.pages ?? 1;
      } else {
        this.messageDialogService.error(res.message || 'No assigned loads found.');
        this.loads = [];
        this.totalPages = 1;
      }
    });
  }

  async markReadyForSettlement(load: FilteredLoad) {
    const deliveryLoad = load as DeliveryLoad;
    // If already in ready_settlement, no-op
    if (deliveryLoad.state === 'ready_settlement') {
      await firstValueFrom(this.messageDialogService.info('This load is already marked as ready for settlement.'));
      return;
    }

    // Prompt user for confirmation
    const confirmed = await firstValueFrom(this.messageDialogService.confirm(
      'Are you ready for settlement?'
    ));
    if (!confirmed) return;

    // If draft, first move to in_transit as workflow prerequisite?
    // If business requires draft -> in_transit before ready_settlement, uncomment below
    // if (deliveryLoad.state === 'draft') {
    //   const ok = await this.setLoadStateWithValidation(deliveryLoad.id, 'in_transit');
    //   if (!ok) return;
    // }

    const ok = await this.setLoadStateWithValidation(deliveryLoad.id, 'ready_settlement');
    if (!ok) {
      // Ask user if they want to force the operation
      const forceConfirmed = await firstValueFrom(this.messageDialogService.confirm(
        'There was an error marking the load as ready for settlement. Do you want to force it? This will create auto-returns for undelivered invoices.'
      ));
      
      if (forceConfirmed) {
        try {
          const forceResponse = await firstValueFrom(this.deliveryService.forceReadyForSettlement(deliveryLoad.id));
          if (forceResponse.status === 'success') {
            await firstValueFrom(this.messageDialogService.success('Load has been forced to ready for settlement.'));
            this.fetchAssignedLoads();
          } else {
            await firstValueFrom(this.messageDialogService.error(forceResponse.message || 'Failed to force ready for settlement.'));
          }
        } catch (err: any) {
          const msg = err?.error?.message || err?.message || 'Error forcing ready for settlement';
          await firstValueFrom(this.messageDialogService.error(msg));
        }
      }
      return;
    }

    await firstValueFrom(this.messageDialogService.success('Marked as ready for settlement.'));
    this.fetchAssignedLoads();
  }

  async viewLoad(load: FilteredLoad) {
    const deliveryLoad = load as DeliveryLoad;

    // 1) If draft → move to in_transit
    if (deliveryLoad.state === 'draft') {
      const ok = await this.validateLoadForTransit(deliveryLoad.id);
      if (!ok) return; // 2) validation errors already shown
    }

    // 3) If ready_settlement → ask to revert back
    if (deliveryLoad.state === 'ready_settlement') {
      const confirmed = await firstValueFrom(this.messageDialogService.info(
        'This load is marked as ready for settlement. You can not change it.'
      ));
      return; // errors already shown
    }

    // Normal operation (unchanged): fetch details and open
    this.openLoadDetails(load.id, true);
  }

  private openLoadDetails(loadId: number, pushHistory: boolean): void {
    this.deliveryService.getLoadDetails(loadId).subscribe(async (res) => {
      if (res.status !== 'success' || !res.load) {
        this.messageDialogService.error(res.message || 'Unable to load details');
        sessionStorage.removeItem(this.lastOpenLoadIdStorageKey);
        return;
      }

      this.selectedLoad = res;
      this.showCart = true;
      sessionStorage.setItem(this.lastOpenLoadIdStorageKey, String(loadId));

      if (pushHistory) {
        this.pushLoadDetailsHistoryState();
      }
    });
  }

  private async setLoadStateWithValidation(loadId: number, state: UpdatableLoadState): Promise<boolean> {
    try {
      const resp = await firstValueFrom(this.deliveryService.setLoadState(loadId, state));
      if (resp.status === 'success') {
        return true;
      }
      await firstValueFrom(this.messageDialogService.error(resp.message || 'Failed to update load state'));
      return false;
    } catch (err: any) {
      const msg = err?.error?.message || err?.message || 'Validation error while updating load state';
      await firstValueFrom(this.messageDialogService.error(msg));
      return false;
    }
  }

  private async validateLoadForTransit(loadId: number): Promise<boolean> {
    try {
      const resp = await firstValueFrom(this.deliveryService.validateReadyForTransit(loadId));
      if (resp.status === 'success') {
        return true;
      }
      await firstValueFrom(this.messageDialogService.error(resp.message || 'Failed to validate load state'));
      return false;
    } catch (err: any) {
      const msg = err?.error?.message || err?.message || 'Validation error while checking load state';
      await firstValueFrom(this.messageDialogService.error(msg));
      return false;
    }
  }

  closeDetails(syncBrowserHistory: boolean = true): void {
    if (!this.showCart) return;

    if (syncBrowserHistory && this.isCurrentLoadDetailsHistoryState()) {
      window.history.back();
      return;
    }

    this.selectedLoad = null;
    this.showCart = false;
    sessionStorage.removeItem(this.lastOpenLoadIdStorageKey);
  }

  @HostListener('window:popstate', ['$event'])
  onBrowserBack(event: PopStateEvent): void {
    if (!this.showCart) return;

    if (
      event.state?.screen === this.loadDetailsHistoryStateKey ||
      event.state?.screen === this.invoiceDetailsHistoryStateKey ||
      event.state?.screen === this.invoiceReturnHistoryStateKey
    ) return;

    this.closeDetails(false);
  }

  private pushLoadDetailsHistoryState(): void {
    if (this.isCurrentLoadDetailsHistoryState()) return;
    window.history.pushState({ screen: this.loadDetailsHistoryStateKey }, '', window.location.href);
  }

  private isCurrentLoadDetailsHistoryState(): boolean {
    return window.history.state?.screen === this.loadDetailsHistoryStateKey;
  }

  nextPage(): void {
    if (this.page < this.totalPages) {
      this.page++;
      this.fetchAssignedLoads();
    }
  }

  prevPage(): void {
    if (this.page > 1) {
      this.page--;
      this.fetchAssignedLoads();
    }
  }
}
